/**
 * The valve report lifecycle as the screens see it. Mirrors
 * backend/services/valveWorkflow.ts: statuses, which stage a status sits
 * in, what each pane is called and which admin list feeds each field.
 * Change the two together.
 */

export type ValveStatus =
  | 'Draft'
  | 'InTesting'
  | 'InspectionReview'
  | 'ClientApproval'
  | 'ClientDeclined'
  | 'InOverhaul'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected'
  | 'Archived';

export const STATUS_LABELS: Record<ValveStatus, string> = {
  Draft: 'Draft',
  InTesting: 'In Testing',
  InspectionReview: 'GRPS Review',
  ClientApproval: 'Awaiting Client',
  ClientDeclined: 'Client Declined',
  InOverhaul: 'In Overhaul',
  PendingApproval: 'Pending Approval',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Archived: 'Archived',
};

/** StatusBadge colour key for each status (StatusBadge owns the colours). */
export const STATUS_BADGE_KEY: Record<ValveStatus, string> = {
  Draft: 'draft',
  InTesting: 'in-progress',
  InspectionReview: 'pending_review',
  ClientApproval: 'awaiting_response',
  ClientDeclined: 'declined',
  InOverhaul: 'in-progress',
  PendingApproval: 'pending_approval',
  Approved: 'approved',
  Rejected: 'rejected',
  Archived: 'archived',
};

export const STATUS_FILTER_OPTIONS = (Object.keys(STATUS_LABELS) as ValveStatus[])
  .filter(s => s !== 'Archived')
  .map(s => ({ value: s, label: STATUS_LABELS[s] }));

export function statusLabel(status: string): string {
  return STATUS_LABELS[status as ValveStatus] || status;
}
export function statusBadgeKey(status: string): string {
  return STATUS_BADGE_KEY[status as ValveStatus] || status;
}

/** The technician may edit in these statuses (the server enforces the same list). */
export const EDITABLE_STATUSES: ValveStatus[] = ['Draft', 'InTesting', 'Rejected', 'ClientDeclined', 'InOverhaul'];

export function isWorkshop(doc: { testType?: string } | null | undefined): boolean {
  return String(doc?.testType || 'workshop') === 'workshop';
}

/** True once the client has approved: the inspection is frozen and the overhaul is being written. */
export function inOverhaulStage(doc: { stage?: string } | null | undefined): boolean {
  return doc?.stage === 'overhaul';
}

/* -------------------------------- panes --------------------------------- */

export type PaneKey = 'valve' | 'actuator' | 'positioner' | 'regulator' | 'solenoid' | 'tubingFitting';

export interface PaneField {
  key: string;
  label: string;
  /** The ValvesConfig.options list behind the dropdown; absent = free text. */
  list?: string;
  /** Fixed choices (no admin list). */
  fixed?: Array<{ value: string; label: string }>;
}

export interface PaneDef {
  key: PaneKey;
  title: string;
  /** Whether a new report starts with the pane switched on. */
  defaultRequired: boolean;
  fields: PaneField[];
}

export const PANES: PaneDef[] = [
  {
    key: 'valve',
    title: 'Valve Details',
    defaultRequired: true,
    fields: [
      { key: 'valveType', label: 'Valve Type', fixed: [{ value: 'general', label: 'General' }, { value: 'control', label: 'Control' }] },
      { key: 'tagNumber', label: 'Tag Number' },
      { key: 'manufacturer', label: 'Manufacturer', list: 'valveManufacturers' },
      { key: 'model', label: 'Model', list: 'valveModels' },
      { key: 'serialNumber', label: 'Serial Number' },
      { key: 'size', label: 'Size', list: 'valveSizes' },
      { key: 'trimMaterial', label: 'Trim Material', list: 'trimMaterials' },
      { key: 'bodyMaterial', label: 'Body Material', list: 'bodyMaterials' },
      { key: 'flowDirection', label: 'Flow Direction', list: 'flowDirections' },
      { key: 'maxCV', label: 'Max CV' },
      { key: 'leakageClass', label: 'Leakage Class', list: 'leakageClasses' },
    ],
  },
  {
    key: 'actuator',
    title: 'Operator Details',
    defaultRequired: true,
    fields: [
      { key: 'manufacturer', label: 'Manufacturer', list: 'actuatorManufacturers' },
      { key: 'model', label: 'Model', list: 'operatorModels' },
      { key: 'serialNumber', label: 'Serial Number' },
      { key: 'type', label: 'Type', list: 'actuatorTypes' },
      { key: 'size', label: 'Size', list: 'operatorSizes' },
      { key: 'maxOperatingPressure', label: 'Max Operating Pressure', list: 'operatorMaxOperatingPressures' },
      { key: 'travel', label: 'Travel', list: 'operatorTravels' },
      { key: 'operatingRange', label: 'Operating Range', list: 'operatorOperatingRanges' },
      { key: 'failMode', label: 'Fail Mode', list: 'failModes' },
    ],
  },
  {
    key: 'positioner',
    title: 'Positioner Details',
    defaultRequired: true,
    fields: [
      { key: 'manufacturer', label: 'Manufacturer', list: 'positionerManufacturers' },
      { key: 'model', label: 'Model', list: 'positionerModels' },
      { key: 'serialNumber', label: 'Serial Number' },
      { key: 'signalRange', label: 'Signal Range', list: 'signalRanges' },
      { key: 'positionerAction', label: 'Positioner Action', list: 'positionerActions' },
    ],
  },
  {
    key: 'regulator',
    title: 'Regulator Details',
    defaultRequired: false,
    fields: [
      { key: 'manufacturer', label: 'Manufacturer', list: 'regulatorManufacturers' },
      { key: 'model', label: 'Model', list: 'regulatorModels' },
      { key: 'serialNumber', label: 'Serial Number' },
      { key: 'type', label: 'Type', list: 'regulatorTypes' },
      { key: 'outputRange', label: 'Output Range', list: 'regulatorOutputRanges' },
    ],
  },
  {
    key: 'solenoid',
    title: 'Solenoid Details',
    defaultRequired: false,
    fields: [
      { key: 'manufacturer', label: 'Manufacturer', list: 'solenoidManufacturers' },
      { key: 'model', label: 'Model', list: 'solenoidModels' },
      { key: 'serialNumber', label: 'Serial Number' },
      { key: 'voltage', label: 'Voltage', list: 'solenoidVoltages' },
      { key: 'type', label: 'Type', list: 'solenoidTypes' },
    ],
  },
  {
    key: 'tubingFitting',
    title: 'Tubing / Fitting Details',
    defaultRequired: false,
    fields: [
      { key: 'manufacturer', label: 'Manufacturer', list: 'tubingManufacturers' },
      { key: 'model', label: 'Model', list: 'tubingModels' },
      { key: 'material', label: 'Material', list: 'tubingMaterials' },
      { key: 'size', label: 'Size', list: 'tubingSizes' },
    ],
  },
];

const PANE_DATA_KEYS: Record<PaneKey, string[]> = Object.fromEntries(
  PANES.map(p => [p.key, p.fields.map(f => f.key).filter(k => k !== 'valveType')]),
) as Record<PaneKey, string[]>;

/** Same rule as the server: the toggle when set, else data present (the valve always). */
export function paneRequired(doc: any, pane: PaneKey): boolean {
  const section = doc?.[pane] || {};
  if (typeof section.required === 'boolean') return section.required;
  if (pane === 'valve') return true;
  return PANE_DATA_KEYS[pane].some(k => String(section[k] ?? '').trim());
}

/* ------------------------------- history -------------------------------- */

export const HISTORY_LABELS: Record<string, string> = {
  created: 'Report created',
  saved: 'Saved',
  inspection_submitted: 'Inspection submitted for GRPS review',
  inspection_approved: 'Inspection approved by GRPS reviewer',
  inspection_rejected: 'Inspection rejected by GRPS reviewer',
  client_email_sent: 'Approval request emailed to client',
  client_email_failed: 'Client email could not be sent',
  client_link_opened: 'Client opened the approval link',
  client_approved: 'Client approved the work',
  client_declined: 'Client declined the work',
  client_file_added: 'File for client added',
  client_file_removed: 'File for client removed',
  submitted: 'Submitted for final approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

export function historyLabel(action: string): string {
  return HISTORY_LABELS[action] || action;
}

export function formatAud(n: unknown): string {
  const num = Number(n);
  if (!Number.isFinite(num)) return '';
  return num.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}
